/**
 * Petticoat Lane resilient predictive search.
 * Keeps type-ahead working independently of Pipeline's optional search controller.
 */
(() => {
  const INPUT = '[data-predictive-search-input]';
  const MIN_QUERY_LENGTH = 2;
  let timer;
  let activeController;

  const clear = (results) => {
    if (!results) return;
    results.replaceChildren();
    results.hidden = true;
  };

  const render = (results, products, query) => {
    results.replaceChildren();
    results.hidden = false;

    const wrapper = document.createElement('div');
    wrapper.className = 'pl-predictive-results';
    wrapper.style.cssText = 'background:#fff;border:1px solid rgba(0,0,0,.2);box-shadow:0 10px 26px rgba(0,0,0,.12);padding:16px;max-height:70vh;overflow:auto;';

    const heading = document.createElement('p');
    heading.textContent = products.length ? 'Products matching "' + query + '"' : 'No products found for "' + query + '"';
    heading.style.cssText = 'margin:0 0 10px;font-size:14px;font-weight:600;';
    wrapper.appendChild(heading);

    if (products.length) {
      const list = document.createElement('div');
      list.style.cssText = 'display:grid;gap:8px;';

      products.forEach((product) => {
        const link = document.createElement('a');
        link.href = product.url;
        link.style.cssText = 'display:grid;grid-template-columns:52px 1fr;gap:10px;align-items:center;color:inherit;text-decoration:none;padding:4px 0;';

        if (product.featured_image?.url) {
          const image = document.createElement('img');
          image.src = product.featured_image.url;
          image.alt = product.featured_image.alt || product.title || '';
          image.width = 52;
          image.height = 66;
          image.loading = 'lazy';
          image.style.cssText = 'width:52px;height:66px;object-fit:cover;';
          link.appendChild(image);
        } else {
          const spacer = document.createElement('span');
          spacer.style.cssText = 'display:block;width:52px;height:66px;background:#f3f3f3;';
          link.appendChild(spacer);
        }

        const copy = document.createElement('span');
        const title = document.createElement('strong');
        title.textContent = product.title || '';
        title.style.cssText = 'display:block;font-size:14px;line-height:1.25;';
        copy.appendChild(title);

        if (product.price) {
          const price = document.createElement('span');
          price.textContent = product.price;
          price.style.cssText = 'display:block;margin-top:3px;font-size:13px;';
          copy.appendChild(price);
        }

        link.appendChild(copy);
        list.appendChild(link);
      });

      wrapper.appendChild(list);
    }

    results.appendChild(wrapper);
  };

  const NON_BRA_INTENT = /\b(?:bra(?:lette)?s?|briefs?|thongs?|pant(?:y|ies)|bikinis?|bottoms?|underwear|garters?|swim(?:wear)?|one[-\s]?piece|tankinis?|bathing|beach)\b/i;

  const fetchProducts = async (query, signal) => {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last'
    });
    const response = await fetch('/search/suggest.json?' + params.toString(), {
      credentials: 'same-origin',
      signal
    });
    if (!response.ok) throw new Error('Suggestion request failed');

    const data = await response.json();
    return data?.resources?.results?.products || [];
  };

  const isBra = (product) => {
    const tags = Array.isArray(product?.tags) ? product.tags : [];
    return tags.some((tag) => String(tag).toLowerCase() === 'bra') || /\bbra\b/i.test(product?.title || '');
  };

  const shouldPrioritizeBras = (query) => {
    const words = query.trim().split(/\s+/);
    return query.length >= 3 && words.length <= 3 && !NON_BRA_INTENT.test(query);
  };

  const braFirst = (products, braMatches) => {
    const seen = new Set();
    return [...braMatches, ...products]
      .filter((product) => product?.id && !seen.has(product.id) && seen.add(product.id))
      .sort((a, b) => Number(isBra(b)) - Number(isBra(a)))
      .slice(0, 8);
  };

  const setup = (input) => {
    if (input.dataset.plPredictiveReady === 'true') return;

    const form = input.closest('form');
    const results = form?.querySelector('[data-predictive-search-results]');
    if (!form || !results) return;

    input.dataset.plPredictiveReady = 'true';
    results.setAttribute('aria-live', 'polite');
    results.hidden = true;

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(timer);
      activeController?.abort();

      if (query.length < MIN_QUERY_LENGTH) {
        clear(results);
        return;
      }

      timer = window.setTimeout(async () => {
        activeController = new AbortController();

        try {
          const products = await fetchProducts(query, activeController.signal);
          let rankedProducts = products;

          if (shouldPrioritizeBras(query)) {
            try {
              const braMatches = await fetchProducts(query + ' bra', activeController.signal);
              rankedProducts = braFirst(products, braMatches);
            } catch (error) {
              if (error.name === 'AbortError') throw error;
            }
          }

          if (input.value.trim() === query) render(results, rankedProducts, query);
        } catch (error) {
          if (error.name !== 'AbortError') clear(results);
        }
      }, 180);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        clear(results);
        input.blur();
      }
    });

    form.addEventListener('submit', (event) => {
      const query = input.value.trim();
      if (!query) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign('/search?q=' + encodeURIComponent(query));
    }, true);

    form.addEventListener('reset', () => window.setTimeout(() => clear(results), 0));
    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) clear(results);
    });
  };

  const refresh = () => document.querySelectorAll(INPUT).forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  new MutationObserver(refresh).observe(document.documentElement, { childList:true, subtree:true });
})();